import React, { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Loader2, ShieldCheck } from "lucide-react";

export default function PasswordReset() {
    const { user, refresh, setSession } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const tokenFromUrl = params.get("token") || "";
    const tokenMode = !!tokenFromUrl;

    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    // If no token in URL and no signed-in user, send to /login
    if (!tokenMode && !user) return <Navigate to="/login" replace />;

    const strength = scorePassword(next);
    const issues = passwordIssues(next, confirm);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (issues.length > 0) {
            setError(issues[0]);
            return;
        }
        setBusy(true);
        try {
            if (tokenMode) {
                const { data } = await api.post("/auth/forgot-password/confirm", {
                    token: tokenFromUrl,
                    new_password: next,
                });
                if (setSession) setSession(data);
                await refresh?.();
                navigate("/app", { replace: true });
            } else {
                await api.post("/auth/password-reset", {
                    current_password: current,
                    new_password: next,
                });
                await refresh?.();
                navigate("/app", { replace: true });
            }
        } catch (e2) {
            const d = e2?.response?.data?.detail;
            setError(typeof d === "string" ? d : d?.message || "Reset failed.");
        } finally {
            setBusy(false);
        }
    };

    const forced = !!user?.must_reset_password;
    const title = tokenMode
        ? "Choose a new password"
        : forced
          ? "Set a new password"
          : "Change your password";
    const blurb = tokenMode
        ? "You are resetting your password via a secure link. The link expires 30 minutes after it was issued."
        : forced
          ? "This is your first sign-in. Choose a strong password to continue."
          : "Update your password. You'll be re-issued a fresh session token.";

    return (
        <div className="min-h-screen bg-[#F5F0E8] flex items-center justify-center p-6" data-testid="password-reset-page">
            <div className="w-full max-w-md">
                <div className="flex items-center gap-2 text-[#0D1B3E]/70">
                    <ShieldCheck size={18} color="#C9A84C" />
                    <span className="label-eyebrow">Security</span>
                </div>
                <h1 className="font-display text-4xl mt-3 tracking-tight">{title}</h1>
                <p className="mt-3 text-[#0D1B3E]/75 text-sm">{blurb}</p>

                <form onSubmit={submit} className="mt-7 space-y-5">
                    {!tokenMode && (
                        <div>
                            <label className="label-eyebrow">Current password</label>
                            <input
                                type="password"
                                className="field mt-2 min-h-[44px]"
                                value={current}
                                onChange={(e) => setCurrent(e.target.value)}
                                autoComplete="current-password"
                                required
                                data-testid="password-reset-current"
                            />
                        </div>
                    )}
                    <div>
                        <label className="label-eyebrow">New password</label>
                        <input
                            type="password"
                            className="field mt-2 min-h-[44px]"
                            value={next}
                            onChange={(e) => setNext(e.target.value)}
                            autoComplete="new-password"
                            required
                            data-testid="password-reset-new"
                        />
                        <PasswordMeter score={strength} />
                    </div>
                    <div>
                        <label className="label-eyebrow">Confirm new password</label>
                        <input
                            type="password"
                            className="field mt-2 min-h-[44px]"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            autoComplete="new-password"
                            required
                            data-testid="password-reset-confirm"
                        />
                    </div>
                    {issues.length > 0 && (
                        <ul className="text-xs text-[#0D1B3E]/65 space-y-1" data-testid="password-reset-hints">
                            {issues.map((it, i) => (
                                <li key={i}>– {it}</li>
                            ))}
                        </ul>
                    )}
                    {error && (
                        <div className="text-sm text-rose-700" data-testid="password-reset-error">
                            {error}
                        </div>
                    )}
                    <button
                        type="submit"
                        className="btn-primary w-full min-h-[44px]"
                        disabled={busy || issues.length > 0}
                        data-testid="password-reset-submit"
                    >
                        {busy ? <Loader2 className="animate-spin" size={14} /> : null}
                        {tokenMode ? "Set new password" : forced ? "Set new password and continue" : "Change password"}
                    </button>
                </form>

                {tokenMode && (
                    <div className="mt-6 text-sm">
                        <Link to="/forgot-password" className="text-[#0D1B3E]/70 hover:text-[#C9A84C] underline">
                            Link expired? Request a new one.
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}

function passwordIssues(pw, confirm) {
    const issues = [];
    if (pw.length < 8) issues.push("At least 8 characters");
    const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((r) => r.test(pw)).length;
    if (classes < 3) issues.push("Use at least three of: lowercase, uppercase, digit, symbol");
    if (pw && confirm && pw !== confirm) issues.push("Passwords do not match");
    return issues;
}

function scorePassword(pw) {
    if (!pw) return 0;
    let score = 0;
    if (pw.length >= 8) score++;
    if (pw.length >= 12) score++;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    return Math.min(score, 5);
}

function PasswordMeter({ score }) {
    const labels = ["Too short", "Weak", "OK", "Strong", "Very strong", "Excellent"];
    const colors = ["bg-rose-400", "bg-rose-400", "bg-amber-400", "bg-sky-400", "bg-emerald-400", "bg-emerald-500"];
    return (
        <div className="mt-2" data-testid="password-strength-meter">
            <div className="h-1 bg-[#0D1B3E]/10 grid grid-cols-5 gap-0.5">
                {[0, 1, 2, 3, 4].map((i) => (
                    <div
                        key={i}
                        className={`h-full ${i < score ? colors[score] : "bg-transparent"}`}
                    />
                ))}
            </div>
            <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-[#0D1B3E]/60">
                {labels[score]}
            </div>
        </div>
    );
}
